import {
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type Ref,
} from 'react';

import type {
  GeneralActivitiesGridResponse,
  GeneralActivityCell,
  GeneralActivityColumn,
  GeneralActivityMember,
  GeneralActivityTeam,
} from '@time-tracking/shared';

import { categoryTypeLabel } from '../../../utils/categoryType.js';
import {
  alsoOnText,
  cellFor,
  columnCoverage,
  gridGroups,
  peopleCount,
  personCellDescription,
  personCellKey,
  personSubLine,
  TEAM_READ_ONLY_REASON,
  teamCellKey,
  teamCheckState,
  wholeTeamFor,
  type GridGroup,
  type TeamCheckState,
} from './model.js';
import { useRovingGrid } from './useRovingGrid.js';

/** What a person checkbox was toggled on — enough for U7b to decide and write. */
export interface PersonCellTarget {
  cellKey: string;
  /** The section the row sits in: a team id, or `NO_TEAM_GROUP_ID`. */
  groupId: string;
  member: GeneralActivityMember;
  activity: GeneralActivityColumn;
  cell: GeneralActivityCell;
}

/** What a heading checkbox acted on. */
export interface TeamCellTarget {
  cellKey: string;
  team: GeneralActivityTeam;
  activity: GeneralActivityColumn;
  state: TeamCheckState;
  wholeTeam: boolean;
}

export interface ActivityMatrixHandle {
  /** Return focus to a cell, e.g. the one that opened a dialog. */
  focusCell: (cellKey: string) => void;
  /**
   * "Jump to team": scroll a section's heading row up under the sticky header
   * and focus its first cell (the heading checkbox, or — for the No team group
   * or an empty team's disabled heading — the first focusable cell after it).
   */
  jumpToGroup: (groupId: string) => void;
}

export interface ActivityMatrixProps {
  grid: GeneralActivitiesGridResponse;
  /** A person checkbox changed (click or Space). Not called for read-only cells. */
  onTogglePerson: (target: PersonCellTarget) => void;
  /** A heading checkbox changed. Not called for read-only or empty teams. */
  onToggleTeam: (target: TeamCellTarget) => void;
  /**
   * Narrows person rows (the name filter). Team heading rows always stay; a
   * team left with no matching rows says so. Defaults to everyone.
   */
  memberFilter?: (member: GeneralActivityMember) => boolean;
  /** The filter text, for the "No one on {Team} matches …" row. */
  filterQuery?: string;
  /**
   * Cells with a write in flight (or one about to be decided, such as a person
   * untick whose impact is being checked): `aria-busy`, unavailable, and their
   * toggles ignored — so a second Space press on a busy cell sends nothing.
   * There is no optimistic update; the box keeps the server's state until the
   * refetch lands.
   */
  busyKeys?: ReadonlySet<string>;
  /** Of `busyKeys`, the cells that show the inline spinner: the ones acted on. */
  spinnerKeys?: ReadonlySet<string>;
  matrixRef?: Ref<ActivityMatrixHandle>;
}

const NONE: ReadonlySet<string> = new Set();

const everyone = () => true;
const SCROLL_STEP_PX = 280;
const EDGE_SLACK_PX = 4;

function ChevronIcon({ dir }: { dir: 'left' | 'right' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {dir === 'right' ? (
        <polyline points="9 18 15 12 9 6" />
      ) : (
        <polyline points="15 18 9 12 15 6" />
      )}
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="12"
      height="12"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

/** The pending write's inline spinner, in the cell's fixed sub-line slot so nothing shifts. */
function CellSpinner() {
  return <span className="ga-spin ga-spin--cell" />;
}

/** One rendered `<tr>`, in order — the source for both the markup and the roving layout. */
type RowModel =
  | { kind: 'teamHead'; group: Extract<GridGroup, { kind: 'team' }> }
  | { kind: 'noTeamHead'; group: Extract<GridGroup, { kind: 'noTeam' }> }
  | { kind: 'message'; groupId: string; text: string }
  | { kind: 'person'; group: GridGroup; member: GeneralActivityMember };

function buildRows(
  groups: readonly GridGroup[],
  memberFilter: (m: GeneralActivityMember) => boolean,
  filterQuery: string,
): RowModel[] {
  const rows: RowModel[] = [];
  for (const group of groups) {
    if (group.kind === 'team') rows.push({ kind: 'teamHead', group });
    else rows.push({ kind: 'noTeamHead', group });
    const name = group.kind === 'team' ? group.team.name : '';
    const visible = group.members.filter(memberFilter);
    if (group.members.length === 0) {
      // An empty No team group needs no message: its heading already reads
      // "0 people", and having everyone on a team is the normal case.
      if (group.kind === 'team') {
        rows.push({
          kind: 'message',
          groupId: group.id,
          text: `No one is on ${name} yet. People appear here once an administrator adds them to the team.`,
        });
      }
    } else if (visible.length === 0) {
      rows.push({
        kind: 'message',
        groupId: group.id,
        text:
          group.kind === 'team'
            ? `No one on ${name} matches “${filterQuery.trim()}”.`
            : `No one on no team matches “${filterQuery.trim()}”.`,
      });
    }
    for (const member of visible) rows.push({ kind: 'person', group, member });
  }
  return rows;
}

export function ActivityMatrix({
  grid,
  onTogglePerson,
  onToggleTeam,
  memberFilter = everyone,
  filterQuery = '',
  busyKeys = NONE,
  spinnerKeys = NONE,
  matrixRef,
}: ActivityMatrixProps) {
  const idPrefix = useId();
  const activities = grid.activities;
  const groups = gridGroups(grid);
  const rows = buildRows(groups, memberFilter, filterQuery);

  // The roving layout mirrors `rows`: a key where a focusable checkbox is
  // rendered, null where there is none (No team heading, a heading over an
  // empty team — natively disabled — and message rows).
  const layout = rows.map((row) =>
    activities.map((a) => {
      if (row.kind === 'teamHead') {
        return row.group.members.length > 0 ? teamCellKey(row.group.id, a.categoryId) : null;
      }
      if (row.kind === 'person') {
        return personCellKey(row.group.id, row.member.userId, a.categoryId);
      }
      return null;
    }),
  );
  const roving = useRovingGrid(layout);

  // "More to the right" / "back to the left" round buttons. No edge shading.
  const scrollRef = useRef<HTMLDivElement>(null);

  // The first focusable cell of each section, for "Jump to team".
  const firstKeyByGroup = new Map<string, string>();
  rows.forEach((row, r) => {
    const groupId = row.kind === 'message' ? row.groupId : row.group.id;
    if (firstKeyByGroup.has(groupId)) return;
    const key = layout[r]?.find((k): k is string => k !== null);
    if (key) firstKeyByGroup.set(groupId, key);
  });
  const jumpToGroup = (groupId: string) => {
    const scroller = scrollRef.current;
    const row = scroller?.querySelector<HTMLElement>(
      // Group ids are UUIDs or NO_TEAM_GROUP_ID: nothing to escape.
      `tr.team-row[data-team="${groupId}"]`,
    );
    if (scroller && row) {
      const headHeight = scroller.querySelector('thead')?.offsetHeight ?? 0;
      // Instant, not smooth: focus follows straight away, and focusing a cell
      // still mid-scroll would yank the view a second time.
      scroller.scrollTop = row.offsetTop - headHeight;
    }
    const key = firstKeyByGroup.get(groupId);
    if (key) roving.focusCell(key);
  };
  // No dependency list: `jumpToGroup` closes over this render's rows.
  useImperativeHandle(matrixRef, () => ({ focusCell: roving.focusCell, jumpToGroup }));
  const [edge, setEdge] = useState({ left: false, right: false });
  const measure = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const left = el.scrollLeft > EDGE_SLACK_PX;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - EDGE_SLACK_PX;
    setEdge((e) => (e.left === left && e.right === right ? e : { left, right }));
  }, []);
  useLayoutEffect(measure);
  useEffect(() => {
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);
  // A scroll button unmounts once its edge is reached. If it held focus, hand
  // focus to the opposite button rather than dropping it to <body> (2.4.3).
  const moreRightRef = useRef<HTMLButtonElement>(null);
  const moreLeftRef = useRef<HTMLButtonElement>(null);
  const focusedMore = useRef<'left' | 'right' | null>(null);
  useEffect(() => {
    if (focusedMore.current === 'right' && !edge.right) moreLeftRef.current?.focus();
    if (focusedMore.current === 'left' && !edge.left) moreRightRef.current?.focus();
  }, [edge]);
  const trackMoreFocus = (side: 'left' | 'right' | null) => () => {
    focusedMore.current = side;
  };
  const scrollBy = (dx: number) => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    scrollRef.current?.scrollBy({ left: dx, behavior: reduce ? 'auto' : 'smooth' });
  };

  const descId = (cellKey: string) => `${idPrefix}-desc-${cellKey}`;
  // Team leads by name, for the "Also on" pill's full text.
  const leadByTeamName = new Map(grid.teams.map((t) => [t.name, t.leadName] as const));

  // Read-only cells stay focusable (aria-disabled, not disabled) so the reason
  // in their description is reachable. Their change handler simply does not
  // call out: the checkbox is controlled, so React puts it straight back.
  function registerCheckbox(cellKey: string, state: TeamCheckState | null) {
    const reg = roving.register(cellKey);
    return (el: HTMLInputElement | null) => {
      // `indeterminate` has no attribute; it can only be set as a property.
      if (el) el.indeterminate = state === 'mixed';
      reg(el);
    };
  }

  function renderTeamHead(group: Extract<GridGroup, { kind: 'team' }>) {
    const { team } = group;
    const n = team.members.length;
    const sub = [
      `Everyone on ${team.name}, including people who join later`,
      ...(team.leadName ? [team.leadName] : []),
      peopleCount(n),
    ].join(' · ');
    return (
      <tr key={`head:${team.teamId}`} className="team-row" data-team={team.teamId}>
        <th scope="row" className="who">
          <div className="ga-team-title">
            <span className="ga-swatch" style={{ background: team.color }} aria-hidden="true" />
            {team.name}
          </div>
          <div className="ga-team-sub">{sub}</div>
        </th>
        {activities.map((a) => {
          const cellKey = teamCellKey(team.teamId, a.categoryId);
          const state = teamCheckState(team, a.categoryId);
          const wholeTeam = wholeTeamFor(team, a.categoryId);
          const joiners = wholeTeam && state !== 'off';
          const empty = n === 0;
          const readOnly = !team.editable;
          const description = [
            ...(joiners ? ['Includes people who join later'] : []),
            ...(readOnly ? [TEAM_READ_ONLY_REASON] : []),
          ].join('. ');
          const target: TeamCellTarget = { cellKey, team, activity: a, state, wholeTeam };
          const busy = busyKeys.has(cellKey);
          const spinning = spinnerKeys.has(cellKey);
          return (
            <td key={a.categoryId} className="c">
              <span className="ga-cell mixed-host">
                <input
                  type="checkbox"
                  className="ga-cb"
                  data-cell-key={cellKey}
                  ref={registerCheckbox(cellKey, state)}
                  tabIndex={roving.tabStopKey === cellKey ? 0 : -1}
                  checked={state === 'on'}
                  aria-checked={state === 'mixed' ? 'mixed' : undefined}
                  disabled={empty}
                  aria-disabled={(readOnly || busy) && !empty ? true : undefined}
                  aria-busy={busy || undefined}
                  aria-label={`Everyone on ${team.name}: ${a.name}${state === 'mixed' ? ', some people' : ''}`}
                  aria-describedby={description ? descId(cellKey) : undefined}
                  onFocus={() => roving.onCellFocus(cellKey)}
                  onChange={() => {
                    if (!readOnly && !empty && !busy) onToggleTeam(target);
                  }}
                />
                {state === 'mixed' && <span className="ga-mixed" aria-hidden="true" />}
                <span className="sub joiners" aria-hidden="true">
                  {spinning ? <CellSpinner /> : joiners ? '+ new joiners' : ''}
                </span>
                {description && (
                  <span id={descId(cellKey)} hidden>
                    {description}
                  </span>
                )}
              </span>
            </td>
          );
        })}
      </tr>
    );
  }

  function renderNoTeamHead(group: Extract<GridGroup, { kind: 'noTeam' }>) {
    return (
      <tr key="head:no-team" className="team-row" data-team={group.id}>
        <th scope="row" className="who">
          <div className="ga-team-title">
            <span className="ga-swatch ga-swatch--none" aria-hidden="true" />
            No team
          </div>
          <div className="ga-team-sub">
            People on no team · individual ticks only · {peopleCount(group.members.length)}
          </div>
        </th>
        {/* No heading checkbox: people on no team get activities only through an
            administrator's individual choice. */}
        {activities.map((a) => (
          <td key={a.categoryId} className="c" />
        ))}
      </tr>
    );
  }

  function renderPerson(group: GridGroup, member: GeneralActivityMember) {
    const others = member.otherTeams;
    const also = others.length > 0 ? alsoOnText(others, leadByTeamName) : null;
    return (
      <tr key={`person:${group.id}:${member.userId}`}>
        <th scope="row" className="who">
          <div className="nm">
            {member.displayName}
            {also && (
              // The label truncates at long team names (D12); the title and the
              // visually hidden full text keep every name (and lead) available.
              <span className="ga-also" title={also.full}>
                <span aria-hidden="true">{also.label}</span>
                <span className="sr-only">{also.full}</span>
              </span>
            )}
          </div>
          {member.utEid && <div className="eid">{member.utEid}</div>}
        </th>
        {activities.map((a) => {
          const cellKey = personCellKey(group.id, member.userId, a.categoryId);
          const cell = cellFor(member, a.categoryId);
          const sub = personSubLine(member, cell);
          // A two-team person appears under each team with the same checkbox
          // name; the description says which listing this one is.
          const listing =
            others.length > 0 && group.kind === 'team' ? `Listed under ${group.team.name}` : '';
          const description = [personCellDescription(member, cell), listing]
            .filter(Boolean)
            .join('. ');
          const readOnly = !cell.editable;
          const busy = busyKeys.has(cellKey);
          return (
            <td key={a.categoryId} className="c">
              <span className="ga-cell">
                <input
                  type="checkbox"
                  className="ga-cb"
                  data-cell-key={cellKey}
                  ref={registerCheckbox(cellKey, null)}
                  tabIndex={roving.tabStopKey === cellKey ? 0 : -1}
                  checked={cell.granted}
                  aria-disabled={readOnly || busy ? true : undefined}
                  aria-busy={busy || undefined}
                  aria-label={`${a.name} for ${member.displayName}`}
                  aria-describedby={description ? descId(cellKey) : undefined}
                  onFocus={() => roving.onCellFocus(cellKey)}
                  onChange={() => {
                    if (!readOnly && !busy) {
                      onTogglePerson({ cellKey, groupId: group.id, member, activity: a, cell });
                    }
                  }}
                />
                {/* The sub-line counts several teams and truncates a long single
                    name (D12); the title keeps the full text for pointer users,
                    and the checkbox's description spells it out for screen readers. */}
                <span
                  className={`sub${sub?.tone === 'stays' ? ' stays' : ''}`}
                  aria-hidden="true"
                  title={sub && !spinnerKeys.has(cellKey) ? sub.title : undefined}
                >
                  {spinnerKeys.has(cellKey) ? <CellSpinner /> : (sub?.text ?? '')}
                </span>
                {description && (
                  <span id={descId(cellKey)} hidden>
                    {description}
                  </span>
                )}
              </span>
            </td>
          );
        })}
      </tr>
    );
  }

  // Rows grouped back into one <tbody> per section.
  const sections: { id: string; rows: RowModel[] }[] = [];
  for (const row of rows) {
    const id = row.kind === 'message' ? row.groupId : row.group.id;
    const last = sections[sections.length - 1];
    if (last && last.id === id) last.rows.push(row);
    else sections.push({ id, rows: [row] });
  }

  return (
    <>
      <div className="ga-scrollwrap">
        <div
          className="ga-scroll"
          ref={scrollRef}
          onScroll={measure}
          role="region"
          aria-label="Activity assignments by person. Scrolls sideways."
          // With no focusable cell (every team empty) the scroller itself takes
          // focus, so keyboard users can still scroll it (axe
          // scrollable-region-focusable).
          tabIndex={roving.tabStopKey === null ? 0 : undefined}
        >
          <table
            className="ga-table"
            role="grid"
            aria-rowcount={rows.length + 1}
            aria-colcount={activities.length + 1}
            onKeyDown={roving.onKeyDown}
          >
            <thead>
              <tr>
                <th className="corner" scope="col">
                  Person
                </th>
                {activities.map((a) => {
                  const { have, total } = columnCoverage(groups, a, memberFilter);
                  return (
                    <th key={a.categoryId} scope="col">
                      <span className="ga-th-name">{a.name}</span>
                      <span className="ga-th-meta">
                        {categoryTypeLabel(a.isWorkTime)} · {have} of {total}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            {sections.map((section) => (
              <tbody key={section.id}>
                {section.rows.map((row) => {
                  switch (row.kind) {
                    case 'teamHead':
                      return renderTeamHead(row.group);
                    case 'noTeamHead':
                      return renderNoTeamHead(row.group);
                    case 'message':
                      return (
                        <tr key={`msg:${row.groupId}`}>
                          <td className="ga-empty-team" colSpan={activities.length + 1}>
                            <span>{row.text}</span>
                          </td>
                        </tr>
                      );
                    case 'person':
                      return renderPerson(row.group, row.member);
                  }
                })}
              </tbody>
            ))}
          </table>
        </div>
        {edge.right && (
          <button
            type="button"
            className="ga-more r"
            ref={moreRightRef}
            onFocus={trackMoreFocus('right')}
            onBlur={trackMoreFocus(null)}
            aria-label="Scroll right to see more activities"
            title="More activities to the right"
            onClick={() => scrollBy(SCROLL_STEP_PX)}
          >
            <ChevronIcon dir="right" />
          </button>
        )}
        {edge.left && (
          <button
            type="button"
            className="ga-more l"
            ref={moreLeftRef}
            onFocus={trackMoreFocus('left')}
            onBlur={trackMoreFocus(null)}
            aria-label="Scroll left"
            title="Back to the first activities"
            onClick={() => scrollBy(-SCROLL_STEP_PX)}
          >
            <ChevronIcon dir="left" />
          </button>
        )}
      </div>
      <div className="ga-kbd">
        <span>
          <kbd>←</kbd> <kbd>→</kbd> <kbd>↑</kbd> <kbd>↓</kbd> move between cells
        </span>
        <span>
          <kbd>Space</kbd> toggles
        </span>
        <span>
          <kbd>Home</kbd> <kbd>End</kbd> jump to first or last activity
        </span>
        {activities.length > 3 && (
          <span className="ga-scrollhint">
            <ArrowIcon /> Scroll the table sideways for more activities
          </span>
        )}
      </div>
    </>
  );
}
